import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { t } from '../i18n';

export interface SlashItem {
  id: string;
  glyph: string;
  label: string;
  desc: string;
  /** The group heading above the item. */
  group?: string;
}

/** Text caret in viewport coordinates, the point the menu hangs from. */
export interface SlashCaret {
  top: number;
  bottom: number;
  left: number;
}

export interface SlashPlacement {
  left: number;
  top?: number;
  bottom?: number;
  maxHeight: number;
  side: 'top' | 'bottom';
}

export const SLASH_MENU_ID = 'slash-menu';
export const slashOptionId = (id: string): string => `slash-option-${id}`;

const SLASH_WIDTH = 330;
const SLASH_MAX_HEIGHT = 320;
/** Below this much room under the caret the menu opens upwards instead. */
const SLASH_MIN_ROOM = 160;
const SLASH_GAP = 4;
const VIEWPORT_MARGIN = 8;

/** Below the caret when there is room, above it otherwise; always kept inside the viewport. */
export function placeSlashMenu(
  caret: SlashCaret,
  viewport: { width: number; height: number },
): SlashPlacement {
  const left = Math.max(
    VIEWPORT_MARGIN,
    Math.min(caret.left, viewport.width - SLASH_WIDTH - VIEWPORT_MARGIN),
  );
  const roomBelow = viewport.height - VIEWPORT_MARGIN - (caret.bottom + SLASH_GAP);
  const roomAbove = caret.top - SLASH_GAP - VIEWPORT_MARGIN;
  if (roomBelow >= Math.min(SLASH_MAX_HEIGHT, SLASH_MIN_ROOM) || roomBelow >= roomAbove) {
    return {
      left,
      top: caret.bottom + SLASH_GAP,
      maxHeight: Math.min(SLASH_MAX_HEIGHT, roomBelow),
      side: 'bottom',
    };
  }
  return {
    left,
    bottom: viewport.height - caret.top + SLASH_GAP,
    maxHeight: Math.min(SLASH_MAX_HEIGHT, roomAbove),
    side: 'top',
  };
}

interface SlashMenuProps {
  items: SlashItem[];
  activeIndex: number;
  onActiveChange: (index: number) => void;
  onPick: (id: string) => void;
  caret: SlashCaret;
  mobile?: boolean;
}

/**
 * The "/" block picker. Focus stays in the editor, which drives the active item with the
 * arrow keys; this only renders the list and takes pointer input.
 */
export function SlashMenu({
  items,
  activeIndex,
  onActiveChange,
  onPick,
  caret,
  mobile = false,
}: SlashMenuProps): React.ReactElement {
  const active = useRef<HTMLButtonElement | null>(null);
  // The visual viewport shrinks when the on-screen keyboard opens. Keeping the
  // menu inside it stops the list from opening behind the keyboard.
  const viewport = window.visualViewport;
  const [mobilePlacement, setMobilePlacement] = useState<SlashPlacement | null>(null);
  const desktopPlacement = placeSlashMenu(caret, {
    width: viewport?.width ?? window.innerWidth,
    height: viewport?.height ?? window.innerHeight,
  });
  const width = Math.min(SLASH_WIDTH, (viewport?.width ?? window.innerWidth) - VIEWPORT_MARGIN * 2);

  useLayoutEffect(() => {
    if (!mobile) return;
    const toolbar = document.querySelector('.insertbar');
    const update = (): void => {
      const visible = window.visualViewport;
      const visibleTop = (visible?.offsetTop ?? 0) + VIEWPORT_MARGIN;
      const visibleLeft = (visible?.offsetLeft ?? 0) + VIEWPORT_MARGIN;
      const visibleBottom =
        (visible?.offsetTop ?? 0) + (visible?.height ?? window.innerHeight) - VIEWPORT_MARGIN;
      const bottom = Math.max(
        visibleTop,
        Math.min(
          visibleBottom,
          (toolbar?.getBoundingClientRect().top ?? visibleBottom) - SLASH_GAP,
        ),
      );
      const height = Math.min(SLASH_MAX_HEIGHT, bottom - visibleTop);
      setMobilePlacement({
        left: visibleLeft,
        top: bottom - height,
        maxHeight: height,
        side: 'top',
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    window.visualViewport?.addEventListener('resize', update);
    window.visualViewport?.addEventListener('scroll', update);
    // The toolbar moves after viewport events and can grow when an action shows feedback.
    const observer = new MutationObserver(update);
    const resizeObserver = new ResizeObserver(update);
    if (toolbar) {
      observer.observe(toolbar, { attributes: true, attributeFilter: ['style'] });
      resizeObserver.observe(toolbar);
    }
    return () => {
      observer.disconnect();
      resizeObserver.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      window.visualViewport?.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('scroll', update);
    };
  }, [mobile]);

  const placement = mobile && mobilePlacement ? mobilePlacement : desktopPlacement;

  useEffect(() => {
    active.current?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, items]);

  const menu = (
    <div
      id={SLASH_MENU_ID}
      className={mobile ? 'slash slash--mobile' : 'slash'}
      role="listbox"
      aria-label={t('slash.label')}
      data-side={placement.side}
      style={{
        left: placement.left,
        width,
        maxHeight: placement.maxHeight,
        ...(placement.top !== undefined ? { top: placement.top } : {}),
        ...(placement.bottom !== undefined ? { bottom: placement.bottom } : {}),
      }}
    >
      {items.length === 0 ? (
        <p className="slash__empty">{t('editor.noResults')}</p>
      ) : (
        <>
          {items.map((item, itemIndex) => {
            const showGroup = item.group && item.group !== items[itemIndex - 1]?.group;
            return (
              <div key={item.id} className="slash__groupblock">
                {showGroup ? <p className="slash__group">{item.group}</p> : null}
                <button
                  ref={itemIndex === activeIndex ? active : undefined}
                  id={slashOptionId(item.id)}
                  type="button"
                  role="option"
                  tabIndex={-1}
                  aria-selected={itemIndex === activeIndex}
                  className={
                    itemIndex === activeIndex ? 'slash__item nb is-active' : 'slash__item nb'
                  }
                  onMouseMove={() => {
                    // Not mouseenter: a list scrolling under a resting pointer must not take over.
                    if (itemIndex !== activeIndex) onActiveChange(itemIndex);
                  }}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    onPick(item.id);
                  }}
                >
                  <span className="slash__glyph" aria-hidden="true">
                    {item.glyph}
                  </span>
                  <span className="slash__text">
                    <span className="slash__name">{item.label}</span>
                    <span className="slash__desc">{item.desc}</span>
                  </span>
                </button>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
  return mobile ? createPortal(menu, document.body) : menu;
}
